import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Download, KeyRound, Clock, UserX, Moon, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const LISTS = [
  { key: "not_set", label: "Not yet set own password", icon: KeyRound, hint: "Given a temporary or reset password but haven't created their own yet." },
  { key: "no_password", label: "Temp expired / no password", icon: Clock, hint: "Can't log in — issue a new temporary password from the Temp Passwords tab." },
  { key: "never_back", label: "Set password, never came back", icon: UserX, hint: "Created their own password but haven't logged in since." },
  { key: "inactive", label: "Inactive 30+ days", icon: Moon, hint: "Last login more than 30 days ago." },
];

function toCSV(name, rows) {
  const head = ["Teacher", "Name", "Std", "Roll", "Last login"];
  const body = [...rows].sort((a, b) => (a.teacher || "zzz").localeCompare(b.teacher || "zzz"))
    .map((r) => [r.teacher || "Unassigned", r.name, r.student_class, r.roll_number, r.last_login ? formatDate(r.last_login) : "Never"]);
  const csv = [head, ...body].map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `ptg_${name}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
}

export default function LoginStatus() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("not_set");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/onboarding/login-status").then(({ data }) => setD(data)).catch(() => toast.error("Could not load login status")); }, []);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d?.[tab] || []).filter((r) => !s || [r.roll_number, r.name, r.teacher].some((v) => (v || "").toLowerCase().includes(s)));
  }, [d, tab, q]);
  if (!d) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div>;
  const cur = LISTS.find((l) => l.key === tab);

  return (
    <div className="space-y-3" data-testid="login-status">
      <div className="rounded-xl bg-green-50 border border-green-200 p-3 flex items-center gap-2 text-sm text-green-800" data-testid="login-active-count">
        <ShieldCheck className="h-4 w-4" /> <b>{d.active_count}</b> students have set their own password and can use their account.
      </div>
      <div className="flex flex-wrap gap-2">
        {LISTS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} className={`px-3 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1 transition-colors ${tab === key ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`login-filter-${key}`}>
            <Icon className="h-3.5 w-3.5" /> {label} ({d[key].length})
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">{cur.hint}</p>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px] max-w-sm"><Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roll, name, teacher" className="pl-8" data-testid="login-search" /></div>
        <Button variant="outline" size="sm" disabled={!rows.length} onClick={() => toCSV(tab, rows)} data-testid="login-download"><Download className="h-4 w-4 mr-1" /> Teacher-wise CSV</Button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-slate-500 py-4" data-testid="login-empty">No students in this list.</p> : (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="max-h-[28rem] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-left sticky top-0"><tr><th className="p-2">Roll</th><th>Name</th><th>Std</th><th>Teacher</th><th>{tab === "not_set" ? "Given" : "Last login"}</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`login-row-${r.id}`}>
                  <td className="p-2 font-semibold">{r.roll_number}</td><td>{r.name}</td><td>{r.student_class || "—"}</td><td className="text-slate-500">{r.teacher || "—"}</td>
                  <td className="text-xs text-slate-500">{tab === "not_set" ? (r.reset_given ? "Reset password" : `Temp · expires ${r.temp_expires ? formatDate(r.temp_expires) : "—"}`) : (r.last_login ? formatDate(r.last_login) : "Never")}</td>
                </tr>))}</tbody></table>
          </div>
          <div className="p-2 text-xs text-slate-500 bg-slate-50">{rows.length} student(s)</div>
        </div>
      )}
    </div>
  );
}
